<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useIAPStore } from '@/stores/iap'
import { useObjectivesStore } from '@/stores/objectives'
import { useHazardsStore } from '@/stores/hazards'
import { useOrgAssignmentStore } from '@/stores/orgAssignment'
import { useMedicalPlanStore } from '@/stores/medicalPlan'
import { useCommsStore } from '@/stores/comms'
import { useSyncStore } from '@/stores/sync'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import FosButton from '@/components/ui/FosButton.vue'
import IAPCover from './iap/IAPCover.vue'
import IAP202 from './iap/IAP202.vue'
import IAP203 from './iap/IAP203.vue'
import IAP205 from './iap/IAP205.vue'
import IAP206 from './iap/IAP206.vue'
import IAP207 from './iap/IAP207.vue'
import IAP208 from './iap/IAP208.vue'

const incidentId = inject<string>('incidentId', '')
const router = useRouter()
const route = useRoute()

const opsStore = useOpsStore()
const iapStore = useIAPStore()
const objectivesStore = useObjectivesStore()
const hazardsStore = useHazardsStore()
const orgAssignmentStore = useOrgAssignmentStore()
const medicalPlanStore = useMedicalPlanStore()
const commsStore = useCommsStore()
const syncStore = useSyncStore()
const authStore = useAuthStore()
const toast = useToastStore()

const STEP_IDS = ['cover', '202', '203', '205', '206', '207', '208'] as const
type StepId = typeof STEP_IDS[number]
const activeStep = ref<StepId>('cover')
const activeIndex = computed(() => STEP_IDS.indexOf(activeStep.value))

function goToStep(id: StepId) { activeStep.value = id }
function goNext() {
  const next = STEP_IDS[activeIndex.value + 1]
  if (next) activeStep.value = next
}
function goPrev() {
  const prev = STEP_IDS[activeIndex.value - 1]
  if (prev) activeStep.value = prev
}

// ─── Step metadata ────────────────────────────────────────────────────────────

type StepStatus = 'complete' | 'partial' | 'empty'

const stepStatuses = computed<Record<StepId, StepStatus>>(() => {
  const objCount = objectivesStore.objectives.length
  const hazCount = hazardsStore.hazards.length
  const chCount = commsStore.channels.length
  const hasIC = !!(orgAssignmentStore.orgAssignment?.incident_commander?.name)
  const medPlan = medicalPlanStore.medicalPlan
  const hasmedical = !!((medPlan?.aid_stations?.length ?? 0) > 0 || (medPlan?.hospitals?.length ?? 0) > 0)

  return {
    cover: 'complete',
    '202': objCount > 0 ? 'complete' : 'empty',
    '203': hasIC ? 'complete' : 'empty',
    '205': chCount > 0 ? 'complete' : 'empty',
    '206': hasmedical ? 'complete' : 'empty',
    '207': 'complete',
    '208': hazCount > 0 ? 'complete' : 'empty',
  }
})

const stepMetas = computed<Record<StepId, string>>(() => {
  const objCount = objectivesStore.objectives.length
  const hazCount = hazardsStore.hazards.length
  const chCount = commsStore.channels.length
  const icName = orgAssignmentStore.orgAssignment?.incident_commander?.name
  const medPlan = medicalPlanStore.medicalPlan
  const stCount = medPlan?.aid_stations?.length ?? 0
  const hospCount = medPlan?.hospitals?.length ?? 0

  return {
    cover: 'Auto-generated',
    '202': objCount > 0 ? `${objCount} objective${objCount === 1 ? '' : 's'}` : 'No objectives yet',
    '203': icName || 'Not started',
    '205': chCount > 0 ? `${chCount} channel${chCount === 1 ? '' : 's'}` : 'No channels yet',
    '206': stCount > 0 || hospCount > 0
      ? `${stCount} station${stCount === 1 ? '' : 's'} · ${hospCount} hospital${hospCount === 1 ? '' : 's'}`
      : 'Not started',
    '207': 'Derived from ICS 203',
    '208': hazCount > 0 ? `${hazCount} hazard${hazCount === 1 ? '' : 's'}` : 'No hazards yet',
  }
})

const STEPS: { id: StepId; code: string; name: string; derived?: boolean }[] = [
  { id: 'cover', code: 'C',   name: 'Cover',         derived: true },
  { id: '202',   code: '202', name: 'Objectives' },
  { id: '203',   code: '203', name: 'Organization' },
  { id: '205',   code: '205', name: 'Communications' },
  { id: '206',   code: '206', name: 'Medical Plan' },
  { id: '207',   code: '207', name: 'Org Chart',     derived: true },
  { id: '208',   code: '208', name: 'Safety' },
]

const completedCount = computed(() =>
  STEPS.filter(s => stepStatuses.value[s.id] === 'complete').length,
)

// ─── Autosave pill ────────────────────────────────────────────────────────────

const saveState = computed<'saving' | 'error' | 'saved'>(() => {
  if (!syncStore.isOnline) return 'error'
  if (syncStore.pendingWrites > 0) return 'saving'
  return 'saved'
})

// ─── OP info display ──────────────────────────────────────────────────────────

const opInfo = computed(() => {
  const op = opsStore.selectedOp
  if (!op) return ''
  const parts: string[] = [`OP ${op.op_number}`]
  if (op.op_start_date) parts.push(op.op_start_date)
  const time = [op.op_start_time, op.op_end_time].filter(Boolean).join('–')
  if (time) parts.push(time)
  return parts.join(' · ')
})

// ─── Last-updated footer ──────────────────────────────────────────────────────

const lastUpdatedLabel = computed(() => {
  const u = authStore.userProfile
  if (!u) return ''
  const initials = `${u.user_last_name}, ${u.user_first_name?.[0] ?? ''}.`
  const now = new Date()
  const hh = String(now.getUTCHours()).padStart(2, '0')
  const mm = String(now.getUTCMinutes()).padStart(2, '0')
  return `Updated ${hh}:${mm}Z · by ${initials}`
})

// ─── Data subscriptions ───────────────────────────────────────────────────────

async function subscribeAll(opId: string) {
  iapStore.subscribeToIAP(incidentId, opId)
  objectivesStore.subscribeToObjectives(incidentId, opId)
  hazardsStore.subscribeToHazards(incidentId, opId)
  orgAssignmentStore.subscribeToOrgAssignment(incidentId, opId)
  medicalPlanStore.subscribeToMedicalPlan(incidentId, opId)
  commsStore.subscribe(incidentId, opId)
  await iapStore.initializeIAP(incidentId, opId)
}

onMounted(async () => {
  if (incidentId && opsStore.selectedOpId) {
    await subscribeAll(opsStore.selectedOpId)
  }
  const stepParam = route.query.step as string
  if (stepParam && (STEP_IDS as readonly string[]).includes(stepParam)) {
    activeStep.value = stepParam as StepId
  }
})

watch(() => opsStore.selectedOpId, async (opId) => {
  if (opId) await subscribeAll(opId)
})

onUnmounted(() => {
  iapStore.teardown()
  objectivesStore.teardown()
  hazardsStore.teardown()
  orgAssignmentStore.teardown()
  medicalPlanStore.teardown()
  commsStore.teardown()
})

// ─── PDF export ───────────────────────────────────────────────────────────────

const canExport = computed(() => syncStore.isOnline && !iapStore.exporting)

async function handleExport() {
  if (!opsStore.selectedOpId) return
  try {
    const url = await iapStore.exportIAP(incidentId, opsStore.selectedOpId)
    window.open(url, '_blank')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Export failed'
    toast.add(message, 'error')
  }
}
</script>

<template>
  <div class="h-full grid" style="grid-template-rows: 48px 1fr; min-height: 0;">
    <!-- Sub-toolbar -->
    <div class="flex items-center gap-2.5 px-4 bg-surface border-b border-border shrink-0">
      <button
        class="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0"
        @click="router.push({ name: 'incident-forms', params: { id: incidentId } })"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Forms
      </button>
      <span class="w-px h-[18px] bg-border shrink-0" />
      <span class="font-headline font-black text-[13px] uppercase tracking-[0.04em] text-on-surface">Incident Action Plan</span>
      <span class="font-mono text-[11px] tracking-[0.06em] text-outline">{{ opInfo }}</span>
      <span class="flex-1" />

      <!-- Autosave pill -->
      <span
        :class="[
          'inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.06em] px-2 py-1 border rounded',
          saveState === 'saving' && 'text-amber-700 bg-amber-50 border-amber-300',
          saveState === 'error'  && 'text-red-700 bg-red-50 border-red-300',
          saveState === 'saved'  && 'text-green-700 bg-green-50 border-green-300',
        ]"
      >
        <span
          :class="['material-symbols-outlined text-[14px]', saveState === 'saving' && 'animate-spin']"
        >{{ saveState === 'saving' ? 'sync' : saveState === 'error' ? 'error_outline' : 'check' }}</span>
        {{ saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Sync failed' : 'All changes saved' }}
      </span>

      <!-- Generate PDF -->
      <FosButton
        v-if="!syncStore.isOnline"
        variant="secondary"
        size="sm"
        disabled
      >
        <span class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
        Generate PDF
      </FosButton>
      <FosButton
        v-else
        variant="primary"
        size="sm"
        :disabled="iapStore.exporting"
        @click="handleExport"
      >
        <span v-if="iapStore.exporting" class="w-3 h-3 border-2 border-white/30 border-t-white animate-spin inline-block mr-1" />
        <span v-else class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
        {{ iapStore.exporting ? 'Generating…' : 'Generate PDF' }}
      </FosButton>
    </div>

    <!-- Body: step rail + edit pane -->
    <div class="grid min-h-0 overflow-hidden" style="grid-template-columns: 240px 1fr;">
      <!-- Step rail -->
      <aside class="border-r border-border flex flex-col overflow-auto bg-surface/55">
        <!-- Completeness header -->
        <div class="px-4 pt-3.5 pb-2.5 flex flex-col gap-1">
          <span class="font-mono text-[10px] tracking-[0.12em] uppercase text-outline">IAP completeness</span>
          <div class="flex items-baseline gap-1.5">
            <strong class="font-headline font-black text-[22px] leading-none">{{ completedCount }}</strong>
            <span class="font-mono text-[12px] text-outline">/ {{ STEPS.length }} sections ready</span>
          </div>
          <div class="h-1 bg-surface-container-high border border-border rounded-none overflow-hidden mt-1">
            <i
              class="block h-full bg-primary transition-all"
              :style="{ width: `${(completedCount / STEPS.length) * 100}%` }"
            />
          </div>
        </div>

        <!-- Step list -->
        <div class="px-2 pb-4 flex flex-col gap-0.5">
          <button
            v-for="step in STEPS"
            :key="step.id"
            :class="[
              'grid gap-2.5 items-center px-2.5 py-2.5 rounded border text-left cursor-pointer transition-colors',
              activeStep === step.id
                ? 'bg-surface border-border shadow-[inset_3px_0_0_theme(colors.primary)]'
                : 'bg-transparent border-transparent hover:bg-surface-container-low',
            ]"
            style="grid-template-columns: 28px 1fr auto;"
            @click="goToStep(step.id)"
          >
            <!-- Status icon -->
            <span
              :class="[
                'w-[22px] h-[22px] rounded-full border inline-flex items-center justify-center font-mono font-bold text-[10px]',
                stepStatuses[step.id] === 'complete' && 'bg-green-600 border-green-600 text-white',
                stepStatuses[step.id] === 'partial'  && 'bg-amber-50 border-amber-500 text-amber-700',
                stepStatuses[step.id] === 'empty' && activeStep === step.id && 'border-primary text-primary bg-surface',
                stepStatuses[step.id] === 'empty' && activeStep !== step.id && 'border-border text-outline bg-surface',
              ]"
            >
              <span v-if="stepStatuses[step.id] === 'complete'" class="material-symbols-outlined text-[14px]">check</span>
              <span v-else>{{ step.code }}</span>
            </span>

            <!-- Name + meta -->
            <span class="flex flex-col gap-px min-w-0">
              <span class="font-headline font-bold text-[13px] text-on-surface leading-tight">{{ step.name }}</span>
              <span
                :class="[
                  'font-mono text-[10px] tracking-[0.06em] truncate',
                  activeStep === step.id ? 'text-primary' : 'text-outline',
                ]"
              >{{ stepMetas[step.id] }}</span>
            </span>

            <!-- Derived badge -->
            <span
              v-if="step.derived"
              class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low"
            >Auto</span>
          </button>
        </div>

        <!-- Rail footer -->
        <div class="mt-auto px-3 py-3 border-t border-border/60 text-center">
          <span class="font-mono text-[10px] tracking-[0.06em] text-outline">{{ lastUpdatedLabel }}</span>
        </div>
      </aside>

      <!-- Edit pane -->
      <div class="overflow-y-auto min-h-0">
        <IAPCover  v-if="activeStep === 'cover'" :incident-id="incidentId" @next="goNext" />
        <IAP202    v-else-if="activeStep === '202'" :incident-id="incidentId" @prev="goPrev" @next="goNext" />
        <IAP203    v-else-if="activeStep === '203'" :incident-id="incidentId" @prev="goPrev" @next="goNext" />
        <IAP205    v-else-if="activeStep === '205'" :incident-id="incidentId" @prev="goPrev" @next="goNext" />
        <IAP206    v-else-if="activeStep === '206'" :incident-id="incidentId" @prev="goPrev" @next="goNext" />
        <IAP207    v-else-if="activeStep === '207'" @prev="goPrev" @next="goNext" />
        <IAP208    v-else-if="activeStep === '208'" :incident-id="incidentId" @prev="goPrev" />
      </div>
    </div>
  </div>
</template>
